import { useEffect, useRef, useState } from 'react'

import ChatSyncService, { type ConversationSyncStatus } from '@/services/chat-sync.service'
import type { Message } from '@/types'

const POLL_INTERVAL_MS = 10_000

const DEFAULT_STATUS: ConversationSyncStatus = {
  conversationId: '',
  syncStatus: 'ok',
  lastMessageAt: null,
  lastRenderedAt: null,
  pendingMessages: 0,
  failedMessages: 0,
  maxLagMs: 0,
  avgLagMs: 0,
  needsReconciliation: false,
}

export function useConversationSync({
  conversationId,
  enabled,
  wsConnected,
  onMessages,
}: {
  conversationId?: string
  enabled: boolean
  wsConnected: boolean
  onMessages: (messages: Message[]) => void
}) {
  const [status, setStatus] = useState<ConversationSyncStatus>(DEFAULT_STATUS)
  const [isReconciling, setIsReconciling] = useState(false)
  const lastHydratedAtRef = useRef(0)

  useEffect(() => {
    if (!enabled || !conversationId) {
      setStatus(DEFAULT_STATUS)
      setIsReconciling(false)
      return
    }

    const currentConversationId = conversationId

    let cancelled = false
    let timer: number | null = null

    async function tick() {
      const nextStatus = await ChatSyncService.status(currentConversationId)
      if (cancelled) return
      setStatus(nextStatus)

      const isStale = Date.now() - lastHydratedAtRef.current > POLL_INTERVAL_MS
      const shouldReconcile = nextStatus.needsReconciliation || nextStatus.syncStatus === 'delayed'
      const shouldPullMessages = shouldReconcile || !wsConnected || isStale

      if (shouldReconcile) {
        setIsReconciling(true)
        try {
          await ChatSyncService.reconcile(currentConversationId)
        } finally {
          if (!cancelled) {
            setIsReconciling(false)
          }
        }
      }

      if (shouldPullMessages) {
        const messages = await ChatSyncService.messages(currentConversationId, { page: 1, per_page: 100 })
        if (cancelled) return
        lastHydratedAtRef.current = Date.now()
        onMessages(messages)
      }
    }

    tick().catch(() => undefined)
    timer = window.setInterval(() => {
      tick().catch(() => undefined)
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      if (timer) {
        window.clearInterval(timer)
      }
    }
  }, [conversationId, enabled, onMessages, wsConnected])

  return {
    status,
    isReconciling,
  }
}
